import { z } from 'zod';
import { normalizeTag } from '../lib/tagUtils.js';

const slugRegex = /^[a-z0-9-]+$/;
const tagRegex = /^[a-z0-9_-]+$/;
const TagSchema = z.string().regex(tagRegex).max(50);

export const TagWeightsSchema = z.record(
  z.string(),
  z.number().min(0.01).max(10),
).default({});

function makeTagArrayInput(maxItems: number) {
  return z.preprocess(
    (raw) => {
      const arr = Array.isArray(raw) ? raw : [];
      return [
        ...new Set(
          arr
            .filter((t): t is string => typeof t === 'string')
            .map(normalizeTag)
            .filter((t) => t.length > 0),
        ),
      ];
    },
    z.array(TagSchema).max(maxItems),
  );
}

// ─── Station ──────────────────────────────────────────────────────────────────

export const StationVisibilitySchema = z.enum(['public', 'private']);
export type StationVisibility = z.infer<typeof StationVisibilitySchema>;

export const StationSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(100),
  slug: z.string().regex(slugRegex).min(1).max(50),
  description: z.string().max(500).optional(),
  ownerUid: z.string(),
  visibility: StationVisibilitySchema,
  tagFilter: z.array(TagSchema).max(20),
  tagExclude: z.array(TagSchema).max(20),
  tagWeights: TagWeightsSchema,
  active: z.boolean(),
  coverArtUrl: z.string().url().optional(),
  currentTrackCoverArtUrl: z.string().url().optional(),
  currentTrackTitle: z.string().max(300).optional(),
  currentTrackArtist: z.string().max(300).optional(),
  listenerCount: z.number().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type Station = z.infer<typeof StationSchema>;

export const CreateStationSchema = z.object({
  name: z.string().min(1).max(100),
  slug: z
    .string()
    .regex(slugRegex, 'Slug must be lowercase alphanumeric with hyphens only')
    .min(1)
    .max(50),
  description: z.string().max(500).optional(),
  visibility: StationVisibilitySchema.default('public'),
  tagFilter: makeTagArrayInput(20),
  tagExclude: makeTagArrayInput(20),
  tagWeights: TagWeightsSchema,
});
export type CreateStationInput = z.infer<typeof CreateStationSchema>;

export const UpdateStationSchema = CreateStationSchema.omit({ slug: true }).partial().extend({
  active: z.boolean().optional(),
  coverArtUrl: z.string().url().optional(),
});
export type UpdateStationInput = z.infer<typeof UpdateStationSchema>;

// ─── Queue / Playback ─────────────────────────────────────────────────────────

/** Represents a track entry in the upcoming queue (visible to station creator) */
export const QueueEntrySchema = z.object({
  position: z.number().int().min(0),
  songId: z.string().uuid(),
  title: z.string(),
  artist: z.string(),
  durationSeconds: z.number().optional(),
  tags: z.array(z.string()),
});
export type QueueEntry = z.infer<typeof QueueEntrySchema>;

/** Now-playing state broadcast via SSE */
export const NowPlayingSchema = z.object({
  stationSlug: z.string(),
  song: z
    .object({
      id: z.string().uuid(),
      title: z.string(),
      artist: z.string(),
      album: z.string().optional(),
      durationSeconds: z.number().optional(),
      tags: z.array(z.string()),
    })
    .nullable(),
  startedAt: z.coerce.date().nullable(),
  trackSessionId: z.string().nullable(),
});
export type NowPlaying = z.infer<typeof NowPlayingSchema>;

export const QueueControlSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('skip') }),
  z.object({ action: z.literal('previous') }),
  z.object({ action: z.literal('jump'), targetPosition: z.number().int().min(0) }),
  z.object({
    action: z.literal('reorder'),
    fromPosition: z.number().int().min(0),
    toPosition: z.number().int().min(0),
  }),
]);
export type QueueControl = z.infer<typeof QueueControlSchema>;
